import type { FC, ReactNode } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box } from '@mui/material'
import NotesOutlined from '@mui/icons-material/NotesOutlined'
import type { ConversationTurnEntry } from '@/index'
import { AgentMode, EventType } from '@/index'
import type { ClaudeSession } from '@/features/events/conversation/state/types'
import {
  getLatestConversationTurn,
  getLatestDisplayableEvent,
  isNotInteractable,
} from '@/features/events/conversation/state/types'
import type {
  FormattedAgentActivity,
  FormattedUserActivity,
} from '@/features/events/activity/utils/types'
import { MostRecentEventPreview } from '@/features/events/pages/events/components/MostRecentEventPreview'
import { SessionCardLayout } from '@/features/events/pages/events/components/SessionCardLayout'
import {
  conversationButtonSx as sharedConversationButtonSx,
  conversationIconSx,
} from '@/features/events/pages/events/components/sessionCardStyles'
// WARNING: Do NOT use ActivityFeedBar here. It internally calls useLatestActivity()
// which subscribes to global 'activity-event' CustomEvents from the SSE stream.
// Using it in the demo would cause production SSE events to override mock animations.
// Always use ActivityFeedContent (prop-driven) for demo rendering.
import { ActivityFeedContent } from '@/features/events/activity/components/ActivityFeedContent'
import { ActivityStatusIndicator } from '@/features/events/activity/components/ActivityStatusIndicator'
import { IdleActivityIndicator } from '@/features/events/activity/components/IdleActivityIndicator'
import { isDemoProcessing } from '@/features/home/demo/utils/isDemoProcessing'

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

interface DemoSessionOrchestratorProps {
  readonly session: ClaudeSession
  readonly currentActivity?: FormattedAgentActivity | FormattedUserActivity | null
  readonly currentEvent?: ConversationTurnEntry | null
  readonly userActivity?: FormattedUserActivity | null
  readonly userReplyText?: string | null
}

export const DemoSessionOrchestrator: FC<DemoSessionOrchestratorProps> = ({
  session,
  currentActivity,
  currentEvent,
  userActivity,
  userReplyText,
}) => {
  const sessionEvent = getLatestDisplayableEvent(session)
  const latestEvent = currentEvent ?? sessionEvent

  const isInteractable = !isNotInteractable(session)
  const hasActivity = currentActivity != null && currentActivity.status !== 'idle'
  const isProcessingEvent = isDemoProcessing(latestEvent?.eventType ?? null)
  const isProcessing = hasActivity || isProcessingEvent
  const showWaitingForInput =
    !hasActivity && isInteractable && isBlockingEventType(latestEvent?.eventType ?? null)

  const canShowConversation = getLatestConversationTurn(session) !== null || session.isTransient

  const conversationSlot = canShowConversation ? (
    <Box component="span" role="img" aria-label="Conversation available" sx={conversationButtonSx}>
      <NotesOutlined sx={conversationIconSx} />
    </Box>
  ) : undefined

  const previewSlot = latestEvent ? (
    <MostRecentEventPreview
      event={latestEvent}
      isProcessing={isProcessing}
      onClick={noop}
      {...(userActivity === undefined ? {} : { userActivity })}
      {...(userReplyText === undefined ? {} : { userReplyText })}
    />
  ) : undefined

  return (
    <SessionCardLayout
      conversation={conversationSlot}
      preview={previewSlot}
      activity={renderDemoActivity(hasActivity, showWaitingForInput, currentActivity)}
    />
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const noop = () => {}

function renderDemoActivity(
  hasActivity: boolean,
  showWaitingForInput: boolean,
  currentActivity: FormattedAgentActivity | FormattedUserActivity | null | undefined,
): ReactNode {
  if (hasActivity && currentActivity != null) {
    return (
      <Box sx={activityContentSx}>
        <Box sx={activityIconSlotSx}>
          <ActivityStatusIndicator status={currentActivity.status} />
        </Box>
        <ActivityFeedContent activity={currentActivity} />
      </Box>
    )
  }

  if (showWaitingForInput) {
    return (
      <Box sx={activityContentSx}>
        <Box sx={activityIconSlotSx}>
          <ActivityStatusIndicator status="system" />
        </Box>
        <ActivityFeedContent activity={WAITING_FOR_INPUT} />
      </Box>
    )
  }

  return <IdleActivityIndicator />
}

function isBlockingEventType(eventType: EventType | null): boolean {
  return (
    eventType === EventType.AGENT_QUESTIONS ||
    eventType === EventType.TOOL_PERMISSION_REQUEST ||
    eventType === EventType.PLAN_APPROVAL_REQUEST
  )
}

const WAITING_FOR_INPUT: FormattedAgentActivity = {
  toolName: '',
  activityContext: 'Claude is waiting for your input',
  status: 'system',
  agentMode: AgentMode.DEFAULT_MODE,
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const activityContentSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
}

const activityIconSlotSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  flexShrink: 0,
}

// Inert (non-interactive) conversation glyph. Reuses the live button's shared sx
// (padding, color, hover scale) so the demo and live card stay identical by
// construction, then layers the inline-flex centering the live IconButton
// provides intrinsically but a Box does not. Carries no onClick — the demo stays
// provider-free. The hover-reveal column is owned by SessionCardLayout.
const conversationButtonSx: SxProps<Theme> = {
  ...sharedConversationButtonSx,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}
